import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ParamKeyTypes, ParamTypes } from './useParamsSelector.types'

export const useParamsSelector = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const getQueryParam = <T = string>(key: ParamKeyTypes) => {
    const searchedKey = searchParams.get(key)
    if (searchedKey === String(null)) return null
    return searchParams.get(key) as T
  }

  const getAllParams = () => {
    const params: ParamTypes[] = []
    searchParams.forEach((value, key) =>
      params.push({ key: key as ParamKeyTypes, value })
    )
    return params
  }

  const removeQueryParam = (key: ParamKeyTypes) => {
    const param = getQueryParam(key)

    if (param) {
      searchParams.delete(key)
      setSearchParams(searchParams)
    }
  }

  const removeAllParams = () => {
    setSearchParams(new URLSearchParams())
  }

  const addQueryParam = (queries: ParamTypes[] | ParamTypes) => {
    const params = Array.isArray(queries) ? queries : [queries]
    setSearchParams(
      params.reduce((acc: { [key: string]: string }, cur) => {
        acc[String(cur.key)] = cur.value
        return acc
      }, {})
    )
  }

  const objParams = Object.assign(
    {},
    ...getAllParams().map(({ key, value }) => ({
      [key]: value
    }))
  )

  return useMemo(
    () => ({
      getAll: getAllParams,
      deleteAll: removeAllParams,
      get: getQueryParam,
      add: addQueryParam,
      delete: removeQueryParam,
      objParams
    }),
    [
      searchParams,
      getAllParams,
      removeAllParams,
      getQueryParam,
      addQueryParam,
      removeQueryParam,
      objParams
    ]
  )
}

export * from './useParamsSelector.types'
